// Client-side image compression for ID uploads. The BFF runs as a Vercel Function whose request
// body is capped at 4.5 MB; both images are base64-encoded into one request, so each must stay well
// under that after ~33% base64 expansion. Downscaling + re-encoding to JPEG in the browser keeps an
// ordinary phone photo (5–12 MB) comfortably under the limit — and JPEG previews inline for the
// provider, which HEIC/large sources may not.

const MAX_EDGE_PX = 2000
const JPEG_QUALITY = 0.8

// Post-compression cap per image. Two images at this size base64-expand to ~4 MB, under the 4.5 MB
// Vercel request limit. Must stay in step with the BFF's idImageSchema bound.
const MAX_UPLOAD_IMAGE_BYTES = 1.5 * 1024 * 1024

export interface CompressedImage {
  base64: string
  contentType: "image/jpeg"
  fileName: string
}

// Downscale to fit MAX_EDGE_PX, re-encode as JPEG, and return the base64 (no data-URL prefix).
export async function compressPhotoIdImage(file: File): Promise<CompressedImage> {
  // No imageOrientation option: EXIF auto-orientation is already the default in every WebKit version,
  // and passing "from-image" rejects on iOS Safari <17.2 (renamed from "none" in 17.2), which our
  // browser targets still include — that would block mobile users from selecting an ID image.
  const bitmap = await createImageBitmap(file)
  try {
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext("2d")
    if (!context) throw new Error("Canvas 2D context is unavailable")
    context.drawImage(bitmap, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY))
    if (!blob) throw new Error("Image compression produced no output")
    if (blob.size > MAX_UPLOAD_IMAGE_BYTES) throw new Error("Image is still too large after compression")

    return { base64: await blobToBase64(blob), contentType: "image/jpeg", fileName: toJpegName(file.name) }
  } finally {
    bitmap.close()
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((typeof reader.result === "string" ? reader.result : "").split(",")[1] ?? "")
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

function toJpegName(name: string): string {
  return `${name.replace(/\.[^.]+$/, "")}.jpg`
}
